import { useEffect } from 'react';

// Lightweight SEO helper to manage title, meta, canonical, OG/Twitter, and JSON-LD
// No external deps to avoid install overhead.

function upsertMeta(attrName, attrValue, content) {
  const head = document.head || document.getElementsByTagName('head')[0];
  let el = head.querySelector(`meta[${attrName}='${attrValue}']`);
  if (!content) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  const head = document.head || document.getElementsByTagName('head')[0];
  let el = head.querySelector(`link[rel='${rel}']`);
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
  const head = document.head || document.getElementsByTagName('head')[0];
  // Remove existing with same id to avoid duplicates on route changes
  if (id) {
    const existing = head.querySelector(`#${id}`);
    if (existing) existing.remove();
  }
  const script = document.createElement('script');
  if (id) script.id = id;
  script.type = 'application/ld+json';
  script.text = JSON.stringify(data);
  head.appendChild(script);
}

// Canonical domain - ALWAYS use this for SEO signals regardless of how page was accessed
// This ensures Google consolidates all URL variants (www, http) to the canonical version
export const CANONICAL_ORIGIN = 'https://domainoptic.com';
export const SITE_NAME = 'DomainOptic';
export const DEFAULT_OG_IMAGE = `${CANONICAL_ORIGIN}/og-domainoptic-audit-domain-tools-2026.png?v=20260719-homepage`;
export const DEFAULT_OG_IMAGE_ALT = 'DomainOptic homepage with Search and AI Visibility, security audit, and domain research actions.';

function normalizePathWithTrailingSlash(path) {
  if (!path || path === '/') return '/';

  const hashIndex = path.indexOf('#');
  const queryIndex = path.indexOf('?');
  let splitIndex = -1;
  if (hashIndex >= 0 && queryIndex >= 0) splitIndex = Math.min(hashIndex, queryIndex);
  else if (hashIndex >= 0) splitIndex = hashIndex;
  else if (queryIndex >= 0) splitIndex = queryIndex;

  const basePath = splitIndex >= 0 ? path.slice(0, splitIndex) : path;
  if (!basePath || basePath === '/') return '/';

  const normalizedBase = basePath.startsWith('/') ? basePath : `/${basePath}`;
  const lastSegment = normalizedBase.split('/').pop();
  if (lastSegment && lastSegment.includes('.')) return normalizedBase;
  return normalizedBase.endsWith('/') ? normalizedBase : `${normalizedBase}/`;
}

// Normalize path to always include trailing slash for directory URLs
// This matches CloudFront redirect behavior and fixes Google canonical issues
function normalizeCanonicalPath(path) {
  if (!path || path === '/') return '/';
  if (!path.startsWith('http')) {
    return normalizePathWithTrailingSlash(path);
  }

  // For absolute URLs, canonicalize DomainOptic host variants to CANONICAL_ORIGIN.
  try {
    const parsed = new URL(path);
    const host = parsed.hostname.toLowerCase();
    if (host === 'domainoptic.com' || host === 'www.domainoptic.com') {
      return `${CANONICAL_ORIGIN}${normalizePathWithTrailingSlash(parsed.pathname)}`;
    }
    return path;
  } catch {
    return path;
  }
}

export function buildCanonicalUrl(path) {
  const normalizedPath = normalizeCanonicalPath(path);
  if (!normalizedPath) return CANONICAL_ORIGIN;
  return normalizedPath.startsWith('http')
    ? normalizedPath
    : `${CANONICAL_ORIGIN}${normalizedPath}`;
}

export default function SEO({
  title,
  description,
  canonicalPath,
  type = 'website',
  ogImage,
  ogImageAlt = DEFAULT_OG_IMAGE_ALT,
  robots,
  noindex = false,
  publishedTime,
  modifiedTime,
  jsonLd, // object or array of objects
}) {
  useEffect(() => {
    const fallbackPath = window?.location?.pathname || '/';
    const normalizedPath = normalizeCanonicalPath(canonicalPath || fallbackPath);
    const canonicalUrl = normalizedPath
      ? (normalizedPath.startsWith('http') ? normalizedPath : `${CANONICAL_ORIGIN}${normalizedPath}`)
      : CANONICAL_ORIGIN;
    const resolvedOgImage = ogImage || DEFAULT_OG_IMAGE;
    const robotsContent = noindex
      ? 'noindex, nofollow'
      : (robots || 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');

    if (title) document.title = title;
    if (description) upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', robotsContent);
    upsertMeta('name', 'googlebot', robotsContent);

    // Canonical
    upsertLink('canonical', canonicalUrl);

    // Open Graph
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:locale', 'en_US');
    if (title) upsertMeta('property', 'og:title', title);
    if (description) upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:image', resolvedOgImage);
    upsertMeta('property', 'og:image:secure_url', resolvedOgImage);
    upsertMeta('property', 'og:image:width', '1200');
    upsertMeta('property', 'og:image:height', '630');
    upsertMeta('property', 'og:image:alt', ogImageAlt);

    // Twitter
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:url', canonicalUrl);
    if (title) upsertMeta('name', 'twitter:title', title);
    if (description) upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', resolvedOgImage);
    upsertMeta('name', 'twitter:image:alt', ogImageAlt);

    // Article-specific tags (ignored on non-article pages)
    const resolvedModifiedTime = modifiedTime || publishedTime;
    upsertMeta('property', 'article:published_time', type === 'article' ? publishedTime : null);
    upsertMeta('property', 'article:modified_time', type === 'article' ? resolvedModifiedTime : null);

    // JSON-LD
    if (jsonLd) {
      document.head.querySelectorAll('script[data-static-route-jsonld="true"]').forEach((script) => script.remove());
      const data = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      upsertJsonLd('seo-jsonld', data.length === 1 ? data[0] : data);
    } else {
      // Remove any prior SEO JSON-LD on pages that don't provide it
      const existing = document.head.querySelector('#seo-jsonld');
      if (existing) existing.remove();
    }
  }, [title, description, canonicalPath, type, ogImage, ogImageAlt, robots, noindex, publishedTime, modifiedTime, jsonLd]);

  return null;
}
